import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ArrowLeft } from "lucide-react";
import logoSvg from "@/assets/logo.svg";

const TermsOfServicePage = () => {
  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header 
        className="border-b border-border px-4 sm:px-6 pb-3 sm:pb-4 sticky top-0 bg-background/95 backdrop-blur-sm z-10"
        style={{ paddingTop: 'max(2.5rem, calc(env(safe-area-inset-top, 0px) + 1rem))' }}
      >
        <div className="flex items-center gap-3 sm:gap-4 max-w-4xl mx-auto">
          <Link to="/auth">
            <Button variant="ghost" size="icon" className="h-9 w-9 shrink-0">
              <ArrowLeft size={18} />
            </Button>
          </Link>
          <div className="flex items-center gap-3">
            <img src={logoSvg} alt="Conciergerie.ae" className="h-8 w-auto" />
            <h1 className="font-serif text-lg sm:text-xl font-semibold text-foreground">
              Terms of Service
            </h1>
          </div>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-4 sm:px-6 py-6 sm:py-10">
        <div className="prose prose-sm sm:prose max-w-none text-foreground">
          <p className="text-muted-foreground text-sm mb-8">Last updated: 1st of January 2026</p>

          <section className="mb-8">
            <h2 className="text-xl font-serif font-semibold text-foreground mb-4">1. INTRODUCTION & ACCEPTANCE OF TERMS</h2>
            <p className="text-muted-foreground leading-relaxed">
              These Terms of Service ("Terms") govern the access to and use of the concierge services provided under the brand Conciergerie.ae (the "Service").
            </p>
            <p className="text-muted-foreground leading-relaxed mt-3">
              By requesting, booking, or using any Service offered by Conciergerie.ae, the client ("Client", "you") acknowledges having read, understood, and accepted these Terms without limitation or reservation.
            </p>
            <p className="text-muted-foreground leading-relaxed mt-3">
              Conciergerie.ae provides complimentary concierge services exclusively to clients of the Sense Group, including but not limited to LegalSense and Sense Properties.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-serif font-semibold text-foreground mb-4">2. LEGAL ENTITY & OWNERSHIP STRUCTURE</h2>
            <p className="text-muted-foreground leading-relaxed">
              Conciergerie.ae is operated under a dual-entity structure:
            </p>
            <div className="mt-4 p-4 bg-muted/30 rounded-lg">
              <p className="font-medium text-foreground">LGSENSE CORPORATE SERVICES PROVIDER LLC</p>
              <p className="text-muted-foreground text-sm mt-1">1004 BayView Tower, Business Bay, Dubai, United Arab Emirates</p>
              <p className="text-muted-foreground text-sm">Phone: +971 54 548 4840</p>
            </div>
            <div className="mt-4 p-4 bg-muted/30 rounded-lg">
              <p className="font-medium text-foreground">Conciergerie LLC</p>
              <p className="text-muted-foreground text-sm mt-1">30 N Gould St Ste N</p>
              <p className="text-muted-foreground text-sm">Sheridan, WY 82801</p>
              <p className="text-muted-foreground text-sm">United States of America</p>
            </div>
            <p className="text-muted-foreground leading-relaxed mt-4">
              Depending on the Client's country of residence and banking capabilities:
            </p>
            <ul className="list-disc list-inside text-muted-foreground mt-2 space-y-1">
              <li>Clients established in the UAE may be invoiced and collected by LGSENSE CORPORATE SERVICES PROVIDER LLC.</li>
              <li>Clients without UAE banking facilities may be invoiced and collected by Conciergerie LLC (Wyoming, USA).</li>
            </ul>
            <p className="text-muted-foreground leading-relaxed mt-3">
              Regardless of the collecting entity, the nature of the Service remains identical and subject to these Terms.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-serif font-semibold text-foreground mb-4">3. NATURE OF THE SERVICE – ROLE AS FACILITATOR ONLY</h2>
            <p className="text-muted-foreground leading-relaxed">
              Conciergerie.ae acts exclusively as an independent intermediary, facilitator, and coordinator between the Client and third-party service providers ("Providers").
            </p>
            <p className="text-muted-foreground leading-relaxed mt-3">Conciergerie.ae:</p>
            <ul className="list-disc list-inside text-muted-foreground mt-2 space-y-1">
              <li>Does not own, operate, manage, or control any Provider;</li>
              <li>Does not provide transportation, accommodation, travel, dining, leisure, or hospitality services directly;</li>
              <li>Does not guarantee availability, quality, safety, legality, or performance of any Provider.</li>
            </ul>
            <p className="text-muted-foreground leading-relaxed mt-3">
              All services are delivered directly by independent third-party Providers, under their own terms, conditions, licenses, and insurance.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-serif font-semibold text-foreground mb-4">4. THIRD-PARTY PROVIDERS & LIMITATION OF LIABILITY</h2>
            <p className="text-muted-foreground leading-relaxed">
              Conciergerie.ae shall not be held liable, directly or indirectly, for any issue, loss, damage, delay, cancellation, accident, injury, or dispute arising from or related to a Provider, including but not limited to:
            </p>
            <ul className="list-disc list-inside text-muted-foreground mt-2 space-y-1">
              <li>Restaurant reservations unavailable, canceled, modified, or downgraded;</li>
              <li>Poor service, overbooking, refusal of entry, or venue policies;</li>
              <li>Vehicle rental issues, mechanical failures, delays, damages, or accidents;</li>
              <li>Flight delays, cancellations, overbooking, baggage loss, or airline strikes;</li>
              <li>Airbnb or short-term rental issues (cleanliness, maintenance, access, cancellation, disputes);</li>
              <li>Hotel, tour, activity, or event-related issues;</li>
              <li>Force majeure events (weather, strikes, government actions, pandemics, etc.).</li>
            </ul>
            <p className="text-muted-foreground leading-relaxed mt-3">
              The contractual relationship exists solely between the Client and the Provider.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-serif font-semibold text-foreground mb-4">5. NO GUARANTEE – BEST EFFORT OBLIGATION</h2>
            <p className="text-muted-foreground leading-relaxed">
              Conciergerie.ae operates under a best-effort obligation, not an obligation of result.
            </p>
            <p className="text-muted-foreground leading-relaxed mt-3">
              While every reasonable effort is made to facilitate bookings and requests:
            </p>
            <ul className="list-disc list-inside text-muted-foreground mt-2 space-y-1">
              <li>Availability is never guaranteed;</li>
              <li>Prices, conditions, and policies are determined solely by the Provider;</li>
              <li>Requests may be declined or unavailable without notice.</li>
            </ul>
            <p className="text-muted-foreground leading-relaxed mt-3">
              Conciergerie.ae shall not be liable for unsuccessful requests or unmet expectations.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-serif font-semibold text-foreground mb-4">6. CLIENT RESPONSIBILITY & DUE DILIGENCE</h2>
            <p className="text-muted-foreground leading-relaxed">
              The Client acknowledges and agrees that:
            </p>
            <ul className="list-disc list-inside text-muted-foreground mt-2 space-y-1">
              <li>It is the Client's responsibility to review and accept the Provider's terms, cancellation policies, and conditions;</li>
              <li>The Client is solely responsible for verifying visas, passports, travel documents, insurance, and eligibility;</li>
              <li>Any dispute, claim, refund request, or legal action must be addressed directly with the Provider.</li>
            </ul>
            <p className="text-muted-foreground leading-relaxed mt-3">
              The Client releases Conciergerie.ae from any civil, commercial, contractual, or criminal liability related to the Provider's services.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-serif font-semibold text-foreground mb-4">6 BIS. ABSENCE OF CONTRACT, INVOICE OR WRITTEN CONFIRMATION</h2>
            <p className="text-muted-foreground leading-relaxed">
              In certain situations, services may be facilitated by Conciergerie.ae without a formal contract, written agreement, or signed invoice between the Client and the third-party Provider.
            </p>
            <p className="text-muted-foreground leading-relaxed mt-3">
              The Client expressly acknowledges and agrees that:
            </p>
            <ul className="list-disc list-inside text-muted-foreground mt-2 space-y-1">
              <li>The absence of a signed contract, written confirmation, invoice, or receipt does not engage the liability of Conciergerie.ae in any manner whatsoever;</li>
              <li>It is the sole responsibility of the Client to request, review, and secure any contractual document, invoice, or proof of payment directly from the Provider;</li>
              <li>Conciergerie.ae has no obligation to verify, enforce, or guarantee the existence, validity, or execution of such documents.</li>
            </ul>
            <p className="text-muted-foreground leading-relaxed mt-3">
              Under no circumstances shall Conciergerie.ae be held responsible for any loss, dispute, misunderstanding, claim, or legal consequence arising from:
            </p>
            <ul className="list-disc list-inside text-muted-foreground mt-2 space-y-1">
              <li>The absence of a formal contract;</li>
              <li>The absence of an invoice or receipt;</li>
              <li>Informal, verbal, or undocumented arrangements between the Client and the Provider.</li>
            </ul>
            <p className="text-muted-foreground leading-relaxed mt-3">
              The Client assumes full civil, legal, and financial responsibility for engaging with a Provider without proper documentation and expressly waives any claim against Conciergerie.ae in this regard.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-serif font-semibold text-foreground mb-4">7. PAYMENTS & FINANCIAL RESPONSIBILITY</h2>
            <p className="text-muted-foreground leading-relaxed">
              Conciergerie.ae does not act as an escrow agent.
            </p>
            <p className="text-muted-foreground leading-relaxed mt-3">
              Any payment collected by Conciergerie.ae is collected on behalf of the Provider or as a facilitation convenience only.
            </p>
            <p className="text-muted-foreground leading-relaxed mt-3">
              Conciergerie.ae shall not be responsible for:
            </p>
            <ul className="list-disc list-inside text-muted-foreground mt-2 space-y-1">
              <li>Refunds refused or delayed by Providers;</li>
              <li>Provider insolvency or bankruptcy;</li>
              <li>Currency fluctuations or banking fees.</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-serif font-semibold text-foreground mb-4">8. NO AGENCY, PARTNERSHIP OR EMPLOYMENT RELATIONSHIP</h2>
            <p className="text-muted-foreground leading-relaxed">
              Nothing in these Terms shall be construed as creating:
            </p>
            <ul className="list-disc list-inside text-muted-foreground mt-2 space-y-1">
              <li>An agency relationship;</li>
              <li>A partnership;</li>
              <li>A joint venture;</li>
              <li>An employment relationship;</li>
            </ul>
            <p className="text-muted-foreground leading-relaxed mt-3">
              between Conciergerie.ae and any Provider.
            </p>
            <p className="text-muted-foreground leading-relaxed mt-3">
              Providers operate independently and under their own responsibility.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-serif font-semibold text-foreground mb-4">9. INDEMNIFICATION</h2>
            <p className="text-muted-foreground leading-relaxed">
              The Client agrees to indemnify and hold harmless Conciergerie.ae, its shareholders, directors, officers, employees, and affiliates from any claim, loss, damage, liability, cost, or expense arising out of:
            </p>
            <ul className="list-disc list-inside text-muted-foreground mt-2 space-y-1">
              <li>Use of the Service;</li>
              <li>Interaction with Providers;</li>
              <li>Breach of these Terms;</li>
              <li>Violation of applicable laws or Provider policies.</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-serif font-semibold text-foreground mb-4">10. LIMITATION OF LIABILITY</h2>
            <p className="text-muted-foreground leading-relaxed">
              To the maximum extent permitted by law:
            </p>
            <p className="text-muted-foreground leading-relaxed mt-3">
              Conciergerie.ae shall not be liable for any indirect, incidental, special, consequential, or punitive damages, including loss of enjoyment, loss of opportunity, loss of profit, or reputational damage.
            </p>
            <p className="text-muted-foreground leading-relaxed mt-3">
              In all cases, Conciergerie.ae's total liability shall be limited to zero, as the Service is provided free of charge.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-serif font-semibold text-foreground mb-4">11. TERMINATION OF SERVICE</h2>
            <p className="text-muted-foreground leading-relaxed">
              Conciergerie.ae reserves the right to suspend or terminate access to the Service at any time, without notice, in case of misuse, abuse, unreasonable demands, or breach of these Terms.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-serif font-semibold text-foreground mb-4">12. GOVERNING LAW & JURISDICTION</h2>
            <p className="text-muted-foreground leading-relaxed">
              These Terms shall be governed by and construed in accordance with:
            </p>
            <ul className="list-disc list-inside text-muted-foreground mt-2 space-y-1">
              <li>The laws of the United Arab Emirates for services rendered by the UAE entity; or</li>
              <li>The laws of the State of Wyoming, USA for services rendered by the US entity,</li>
            </ul>
            <p className="text-muted-foreground leading-relaxed mt-3">
              without regard to conflict of law principles.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-serif font-semibold text-foreground mb-4">13. CONTACT INFORMATION</h2>
            <p className="text-muted-foreground leading-relaxed">
              For any inquiry related to the Service or these Terms:
            </p>
            <a 
              href="mailto:booking@conciergerie.ae" 
              className="text-primary font-medium hover:underline"
            >
              booking@conciergerie.ae
            </a>
          </section>
        </div>
      </main>

      {/* Footer */}
      <footer 
        className="border-t border-border py-6 text-center"
        style={{ paddingBottom: 'max(1.5rem, calc(env(safe-area-inset-bottom, 0px) + 1rem))' }}
      >
        <p className="text-sm text-muted-foreground">
          © {new Date().getFullYear()} Conciergerie.ae. All rights reserved.
        </p>
      </footer>
    </div>
  );
};

export default TermsOfServicePage;
